import React, { FC, useMemo, useState } from 'react';

import { useFormikContext } from 'formik';

import styled from 'styled-components';

// constants
import aampeTheme from '../../../constants/aampeTheme';

//helpers
import useAsyncDebounce from '../../../helpers/debounce';

// hooks
import useAttributesForSelectQuery from '../../../hooks/filters/useAttributesForSelectQuery';

// models
import {
    AudienceSearchableSelectType,
    ContactAttributeModel,
    FieldType,
} from '../../../models/contact_attributes.model';
import { AttributePlaceholderFormikValues } from '../../../models/formula.model';

// components
import SearchableSelect, {
    SearchableSelectType,
} from '../../inputs/searchableSelect';
import AampeItalicText from '../AampeText/AampeItalicText';

interface AampeAttributeDropdownProps {
    onChange?: (value: string) => void;
    isDisabled?: boolean;
    label?: string;
    showLabel?: boolean;
}

const AampeAttributeDropdown: FC<AampeAttributeDropdownProps> = ({
    onChange,
    isDisabled = false,
    showLabel = true,
    label,
}) => {
    const { setFieldValue, values } =
        useFormikContext<AttributePlaceholderFormikValues>();

    const [searchAttributeValue, setSearchAttributeValue] =
        useState<string>('');

    const menuPortalTarget =
        typeof document !== 'undefined' ? document.body : null;

    const handleDebounce = useAsyncDebounce((value: string) => {
        setSearchAttributeValue(value);
    }, 500);

    const handleAttributeValueChange = (value: string) => {
        handleDebounce(value);
    };
    const {
        data: attributesData,
        isLoading,
        hasNextPage: hasNextPage,
        fetchNextPage: fetchNextPage,
        isFetchingNextPage: isFetchingNextPage,
    } = useAttributesForSelectQuery({
        is_active: true,
        sort_by_name: true,
        attribute_type: FieldType.STR,
        ...(searchAttributeValue
            ? { attribute_name: searchAttributeValue }
            : {}),
    });
    const attributes = useMemo<ContactAttributeModel[]>(() => {
        if (attributesData) {
            return (attributesData.pages ?? []).reduce((acc, curr) => {
                acc = [...acc, ...(curr?.items ?? [])];
                return acc;
            }, []);
        }
        return [];
    }, [attributesData]);

    const handleLoadMore = () => {
        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    };
    const attributeOptions = useMemo<AudienceSearchableSelectType[]>(() => {
        return attributes.map((attribute: ContactAttributeModel) => {
            return {
                value: attribute?.id,
                label: attribute?.attribute_name,
            };
        });
    }, [attributes]);

    return (
        <FieldContainer hasLabel={showLabel}>
            <TitleName>
                {showLabel ? (
                    label ? (
                        label
                    ) : (
                        <>
                            User attribute{' '}
                            <AampeItalicText text={'(Required)'} />
                        </>
                    )
                ) : null}
            </TitleName>
            <SearchableSelect
                isLoading={isLoading}
                name={`userAttribute`}
                options={attributeOptions}
                onChange={(field, value) => {
                    setFieldValue(field, (value as SearchableSelectType).label);
                    onChange?.((value as SearchableSelectType).label);
                }}
                value={attributeOptions.find(
                    (item) => item.label === values.userAttribute,
                )}
                placeholder={'Select attribute or search one'}
                onMenuScrollToBottom={handleLoadMore}
                onInputChange={handleAttributeValueChange}
                isDisabled={isDisabled}
                menuPortalTarget={menuPortalTarget}
                menuPosition="fixed"
            />
        </FieldContainer>
    );
};

const FieldContainer = styled.div<{ hasLabel: boolean }>(({ hasLabel }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(hasLabel ? 2 : 0),
}));

const TitleName = styled.span({
    fontSize: aampeTheme.size.font.default,
    lineHeight: aampeTheme.size.lineHeight.small,
    color: aampeTheme.colors.neutral09,
});

export default AampeAttributeDropdown;
